<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { cloneDeep, compact, flatMap, pickBy, uniq } from 'lodash-es';
import { useTemplateRefsList } from '@vueuse/core';
import { AssetType, BorrowerType, TrusteeType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { getBorrowerDisplayName } from '@/helpers/builder';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import api from '@/api';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { useFinancialCollectionRules } from '@/helpers/useFinancialCollectionRules';
import { useHouseholdApplicants } from '@/helpers/useHouseholdApplicants';
import { toast } from '@/helpers/toast';
import AssetForm from './modules/Assets/AssetForm.vue';
import AssetsStatusCard from './modules/Assets/AssetsStatusCard.vue';
import type { ApplicantId, PropertyAsset, ApplicantAsset } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import type { IconName } from '@/helpers/icons';

const { authorisedUser, loanApp, loanApplicants, state, saveAndGoNext, goBack } = useErhlLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const { flagSessionExpired } = useSessionExpiry();

const collectionRules = useFinancialCollectionRules(loanApp);

const isSecondaryDeclaration = false;
const isIncomeExpenseRequest = false;
const isLoading = ref(false);

const hasNonIndividualBorrower = computed(
  () =>
    loanApp.value.borrowerGroups?.some((g) => g.type === BorrowerType.COMPANY || g.type === BorrowerType.TRUST) ??
    false,
);

/** Prefix text for the assets heading, tailored to borrower composition */
const assetsHeadingPrefix = computed(() => {
  if (hasNonIndividualBorrower.value) {
    return "Please outline the borrower's";
  }
  return loanApp.value.applicants.length > 1 ? 'Please outline the applicants' : "Please outline the applicant's";
});

const showCommercialAssetsUI = computed(() => hasNonIndividualBorrower.value);

// End debt check - show residual debt fields when endDebt > 0
const hasEndDebt = computed(() => loanApp.value.endDebt > 0);

// Use financial collection rules for liabilities
// For "assets only" scenarios (Single Security + No End Debt + Exit=Sale), liabilities are skipped
const shouldCollectLiabilities = computed(() => collectionRules.shouldCollectLiabilities.value);
const isAssetsOnlyScenario = computed(
  () => collectionRules.shouldCollectAssets.value && !collectionRules.shouldCollectLiabilities.value,
);

/**
 * Check if a borrower group is a Corporate Trust (Trust with Company trustee)
 */
const isCorporateTrust = (group: BorrowerGroup): boolean =>
  group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.COMPANY;

/**
 * Get entity borrower groups that need entity-level assets (Company and Corporate Trust)
 * These entities collect assets only (no liabilities)
 */
const entityBorrowerGroups = computed<BorrowerGroup[]>(() => {
  if (!showCommercialAssetsUI.value) return [];

  return (loanApp.value.borrowerGroups ?? []).filter(
    (group) => group.type === BorrowerType.COMPANY || isCorporateTrust(group),
  );
});

const getEntityName = (group: BorrowerGroup): string => {
  if (group.type === BorrowerType.COMPANY) {
    return group.companyDetails?.companyName ?? group.entityName ?? 'Company';
  }
  if (isCorporateTrust(group)) {
    return group.trustDetails?.trustName ?? group.entityName ?? 'Corporate Trust';
  }
  return group.entityName ?? 'Entity';
};

const getEntityIcon = (group: BorrowerGroup): IconName => {
  if (group.type === BorrowerType.COMPANY) return 'company';
  if (isCorporateTrust(group)) return 'trust';
  return 'customer';
};

// Get all applicants from borrowerGroups for commercial flow
// Uses financial collection rules to determine which groups should show assets
const commercialAssetApplicants = computed<ApplicantId[]>(() => {
  if (!showCommercialAssetsUI.value) return [];

  const ids = (loanApp.value.borrowerGroups ?? [])
    .filter((group) => collectionRules.shouldShowAssetsForGroup(group))
    .flatMap((group) => group.applicants?.map((a) => a.applicantId as ApplicantId) ?? []);

  return [...new Set(ids)];
});

const borrowerGroupParticipants = computed<Record<string, string>>(() => {
  if (!showCommercialAssetsUI.value) return {};
  return (loanApp.value.borrowerGroups ?? []).reduce((obj, group) => {
    obj[group.id] = getBorrowerDisplayName(group, loanApp.value.applicants);
    return obj;
  }, {} as Record<string, string>);
});

const { validApplicants } = useHouseholdApplicants(loanApp, authorisedUser, {
  isSecondaryDeclaration,
  isIncomeExpenseRequest,
});

const validApplicantIds = new Set(loanApp.value.applicants.map((a) => a.id));
const validGroupIds = new Set((loanApp.value.borrowerGroups ?? []).map((g) => g.id));
const financials = ref(pickBy(cloneDeep(loanApp.value.financials), (_v, id) => validApplicantIds.has(id)));

// Entity-level assets for Company and Corporate Trust (keyed by borrowerGroupId)
const entityAssets = ref<Record<string, ApplicantAsset[]>>(
  pickBy(cloneDeep(loanApp.value.entityAssets ?? {}), (_v, id) => validGroupIds.has(id)),
);

// Dummy liabilities for entity forms (entities don't have liabilities, but the form component needs it)
const emptyLiabilities = ref<Record<string, never[]>>({});

// Reactively initialize empty arrays for entity groups when they change
watchEffect(() => {
  entityBorrowerGroups.value.forEach((group) => {
    if (!entityAssets.value[group.id]) {
      entityAssets.value[group.id] = [];
    }
    if (!emptyLiabilities.value[group.id]) {
      emptyLiabilities.value[group.id] = [];
    }
  });
});

const assetRefs = useTemplateRefsList<InstanceType<typeof AssetForm>>();
const entityAssetRefs = useTemplateRefsList<InstanceType<typeof AssetForm>>();

// Active tab can be either an applicantId or a borrowerGroupId (for entities)
const activeTab = ref<string | undefined>(undefined);

const isApplicantComplete = (applicantId: ApplicantId): boolean => {
  const applicantFinancials = financials.value[applicantId];
  if (!applicantFinancials) return false;

  return applicantFinancials.assets.length > 0 || applicantFinancials.liabilities.length > 0;
};

const isEntityComplete = (groupId: string): boolean => {
  const assets = entityAssets.value[groupId];
  return assets && assets.length > 0;
};

const validateAssets = async () => {
  let isValid = true;

  const refCount = assetRefs.value.length;
  for (let i = 0; i < refCount; i++) {
    isValid = (await assetRefs.value[i].update()) ?? false;
    if (!isValid) {
      activeTab.value = assetRefs.value[i].getApplicantId();
      break;
    }
  }

  if (isValid) {
    const entityRefCount = entityAssetRefs.value.length;
    for (let i = 0; i < entityRefCount; i++) {
      isValid = (await entityAssetRefs.value[i].update()) ?? false;
      if (!isValid) {
        activeTab.value = entityAssetRefs.value[i].getApplicantId();
        break;
      }
    }
  }

  const addressKey = ({ addressLine, addressLine2, locality, state, postcode }: PropertyAsset['address']): string =>
    compact([addressLine, addressLine2, locality, state, postcode]).join('|').toLowerCase();

  const individualAddressKeys = flatMap(financials.value, (applicant) =>
    applicant.assets
      .filter((asset): asset is PropertyAsset => asset.type === 'property' && 'address' in asset)
      .map((asset) => addressKey(asset.address)),
  );

  const entityAddressKeys = flatMap(entityAssets.value, (assets) =>
    assets
      .filter((asset): asset is PropertyAsset => asset.type === 'property' && 'address' in asset)
      .map((asset) => addressKey(asset.address)),
  );

  const addressKeys = [...individualAddressKeys, ...entityAddressKeys];
  const hasDuplicateAddress = uniq(addressKeys).length !== addressKeys.length;

  if (hasDuplicateAddress) {
    isValid = false;
    window.scrollTo(0, 0);
    toast({
      type: 'danger',
      title: 'Error',
      description: 'Please only enter a property asset once.',
    });
  }

  return isValid;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (item: any) => {
  let description = '';
  if (item.type === AssetType.PROPERTY) {
    description = item.address?.addressLine;
  } else if (item.institution) {
    description = item.institution;
  } else if (item.model) {
    description = `${item.make} ${item.model}`;
  } else if (item.description) {
    description = item.description;
  } else if (item.details) {
    description = item.details;
  }

  return `${item.type.charAt(0).toUpperCase() + item.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  const assets = financials.value[applicantId].assets.map((item) => {
    return cardTitle(item);
  });
  const liabilities = financials.value[applicantId].liabilities.map((item) => {
    return cardTitle(item);
  });

  return [...assets, ...liabilities];
};

const saveAndContinue = async () => {
  if (await validateAssets()) {
    isLoading.value = true;
    let applicationDraftId = loanApp.value.applicationDraftId;
    try {
      if (loanApp.value.brokerId) {
        const savedDraftId = await saveApplicationToDraft(state.value.context, {
          ...loanApp.value,
          financials: financials.value,
          entityAssets: entityAssets.value,
        });
        applicationDraftId = savedDraftId ?? applicationDraftId;
      }
      saveAndGoNext({
        financials: financials.value,
        entityAssets: entityAssets.value,
        applicationDraftId,
      });
    } catch (error) {
      if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
        flagSessionExpired();
        return;
      }
      console.error('Failed to save draft:', error);
    } finally {
      isLoading.value = false;
    }
  }
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        <template v-if="isAssetsOnlyScenario">
          {{ assetsHeadingPrefix }}
          <BxHighlight>assets</BxHighlight>.
        </template>
        <template v-else>
          {{ assetsHeadingPrefix }}
          <BxHighlight>assets</BxHighlight>
          and
          <BxHighlight>liabilities</BxHighlight>.
        </template>
      </h2>
    </div>

    <!-- Assets Only Scenario Message -->
    <div v-if="isAssetsOnlyScenario" class="my-6">
      <p>Based on the exit strategy, only asset details are required for this application.</p>
    </div>

    <!-- Commercial Flow Message -->
    <div v-else-if="showCommercialAssetsUI" class="my-6">
      <p>For assets and liabilities, we will ask based on each director/individual regardless of ABN/Trading Status.</p>
    </div>

    <!-- Original Flow Message -->
    <div v-else class="my-6">
      <p>Enter assets and liabilities for each applicant by clicking "Add assets"</p>
    </div>

    <!-- Commercial Flow: Entity-level assets (Company & Corporate Trust) -->
    <div v-if="showCommercialAssetsUI && entityBorrowerGroups.length > 0" class="my-6 grid gap-4">
      <div v-for="group in entityBorrowerGroups" :key="group.id">
        <BxCard :class="group.id !== activeTab && 'hidden'">
          <h2 class="mb-4 flex items-center text-xl font-medium">
            <span class="mr-3 hidden md:block">
              <BxIcon :name="getEntityIcon(group)" />
            </span>
            {{ getEntityName(group) }}
          </h2>
          <AssetForm
            :ref="entityAssetRefs.set"
            v-model:assets="entityAssets[group.id]"
            v-model:liabilities="emptyLiabilities[group.id]"
            :data-test-id="`entity-asset-form-${group.id}`"
            :applicant-id="group.id"
            :animate="group.id === activeTab"
            :has-end-debt="hasEndDebt"
            :show-liabilities="false"
            :ownership-participants="borrowerGroupParticipants"
            :show-buttons="entityBorrowerGroups.length > 1 || commercialAssetApplicants.length > 0"
            @save="activeTab = undefined"
          />
        </BxCard>

        <AssetsStatusCard
          v-if="group.id !== activeTab"
          :title="getEntityName(group)"
          :is-complete="isEntityComplete(group.id)"
          :button-label="isEntityComplete(group.id) ? 'Edit' : 'Add assets'"
          :is-disabled="Boolean(activeTab && activeTab !== group.id)"
          :icon="getEntityIcon(group)"
          :data-test-id="`entity-assets-status-${group.id}`"
          @edit="activeTab = group.id"
        />
      </div>
    </div>

    <!-- Commercial Flow: Director/Trustee/Individual assets and liabilities -->
    <div v-if="showCommercialAssetsUI" class="my-6 grid gap-4">
      <div v-for="applicantId in commercialAssetApplicants" :key="applicantId">
        <BxCard :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 flex items-center text-xl font-medium">
            <span class="mr-3 hidden md:block">
              <BxIcon name="customer" />
            </span>
            {{ getFullName(loanApplicants[applicantId]) }}
          </h2>
          <AssetForm
            :ref="assetRefs.set"
            v-model:assets="financials[applicantId].assets"
            v-model:liabilities="financials[applicantId].liabilities"
            :data-test-id="`applicant-asset-form-${applicantId}`"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :has-end-debt="hasEndDebt"
            :show-liabilities="shouldCollectLiabilities"
            :ownership-participants="borrowerGroupParticipants"
            :show-buttons="commercialAssetApplicants.length > 1 || entityBorrowerGroups.length > 0"
            @save="activeTab = undefined"
          />
        </BxCard>

        <AssetsStatusCard
          v-if="applicantId !== activeTab"
          :title="getFullName(loanApplicants[applicantId])"
          :is-complete="isApplicantComplete(applicantId)"
          :button-label="isApplicantComplete(applicantId) ? 'Edit' : 'Add assets'"
          :is-disabled="Boolean(activeTab && activeTab !== applicantId)"
          icon="customer"
          :data-test-id="`applicant-assets-status-${applicantId}`"
          @edit="activeTab = applicantId"
        />
      </div>
    </div>

    <!-- Original Flow: Use validApplicants -->
    <div v-else class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <BxCard :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicantId]) }}
          </h2>
          <AssetForm
            :ref="assetRefs.set"
            v-model:assets="financials[applicantId].assets"
            v-model:liabilities="financials[applicantId].liabilities"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :has-end-debt="hasEndDebt"
            :show-liabilities="shouldCollectLiabilities"
            :show-buttons="validApplicants.length > 1"
            @save="activeTab = undefined"
          />
        </BxCard>

        <BxCard :class="`${applicantId === activeTab && 'hidden'} asset-card`">
          <div class="flex items-center">
            <div class="card-details">
              <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
            </div>
            <div class="asset-descriptions ml-6">
              <div v-for="description in cardDescription(applicantId)" :key="description">
                <span class="text-gray-400">{{ description }}</span>
              </div>
            </div>

            <div class="card-button">
              <BxButton
                :id="applicantId + '-assets-start-add'"
                variant="secondary"
                :disabled="
                  (activeTab && activeTab !== applicantId) ||
                  (loanApplicants[applicantId].type === 'primary' && isSecondaryDeclaration)
                "
                @click="activeTab = applicantId"
              >
                {{ financials[applicantId].assets.length ? 'Edit' : 'Add' }} assets
              </BxButton>
            </div>
          </div>
        </BxCard>

        <BxCard :class="`${applicantId === activeTab && 'hidden'} asset-card-mobile`">
          <div class="items-left flex flex-col">
            <div class="card-details">
              <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
            </div>
            <div class="asset-descriptions ml-6">
              <div v-for="description in cardDescription(applicantId)" :key="description">
                <span class="text-gray-400">{{ description }}</span>
              </div>
            </div>
          </div>
          <div class="card-button">
            <BxButton
              :id="applicantId + '-assets-start-add'"
              variant="secondary"
              :disabled="
                (activeTab && activeTab !== applicantId) ||
                (loanApplicants[applicantId].type === 'primary' && isSecondaryDeclaration)
              "
              @click="activeTab = applicantId"
            >
              {{ financials[applicantId].assets.length ? 'Edit' : 'Add' }} assets
            </BxButton>
          </div>
        </BxCard>
      </div>
    </div>

    <div class="mt-6">
      <BxButton
        id="assets-continue"
        data-test-id="assets-continue"
        variant="primary"
        :loading="isLoading"
        @click="saveAndContinue()"
      >
        {{ showCommercialAssetsUI ? 'Continue' : 'Save and continue' }}
      </BxButton>
      <BxButton data-test-id="assets-back" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.asset-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}

.asset-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.asset-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;

  @media (min-width: 767px) {
    text-align: right;
  }

  @media (max-width: 767px) {
    text-align: center;
  }

  flex-basis: 200px;
}
</style>
